/**
 * Demand (matter) currency — ISO codes used for owed amounts and future gateway charges.
 * Account profile Currency is the default; each demand can override for cross-border balances.
 */

export const MATTER_CURRENCIES = Object.freeze([
  { value: 'USD', label: 'USD — US dollar' },
  { value: 'CAD', label: 'CAD — Canadian dollar' },
  { value: 'GBP', label: 'GBP — British pound' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'AUD', label: 'AUD — Australian dollar' },
  { value: 'NZD', label: 'NZD — New Zealand dollar' },
]);

export const MATTER_CURRENCY_CODES = Object.freeze(
  MATTER_CURRENCIES.map((item) => item.value)
);

const CODE_SET = new Set(MATTER_CURRENCY_CODES);

/**
 * @param {unknown} value
 * @param {string} [fallback='USD']
 * @returns {string}
 */
export function normalizeMatterCurrency(value, fallback = 'USD') {
  const raw = String(value || '')
    .trim()
    .toUpperCase();
  if (CODE_SET.has(raw)) return raw;
  const fb = String(fallback || 'USD')
    .trim()
    .toUpperCase();
  return CODE_SET.has(fb) ? fb : 'USD';
}

/**
 * Soft currency symbol for the selected ISO code (e.g. $, CA$, £, €).
 * @param {unknown} currency
 * @returns {string}
 */
export function currencyDisplaySymbol(currency = 'USD') {
  const code = normalizeMatterCurrency(currency);
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value || code;
  } catch {
    return code;
  }
}

export function currencyHintForCountry(country) {
  const c = String(country || '')
    .trim()
    .toUpperCase();
  if (c === 'US' || c === 'USA') return 'USD';
  if (c === 'CA' || c === 'CAN') return 'CAD';
  if (c === 'GB' || c === 'UK') return 'GBP';
  if (c === 'IE' || c === 'IRL') return 'EUR';
  if (c === 'AU' || c === 'AUS') return 'AUD';
  if (c === 'NZ' || c === 'NZL') return 'NZD';
  return null;
}

/**
 * Format a demand/accounting amount. Always keeps two decimal places by default
 * ($664.00, not $664) whether the value came from extract, manual entry, or sync.
 * Callers may override fraction digits (e.g. compact KPI notation).
 *
 * @param {unknown} value
 * @param {unknown} [currencyOrMatter]
 * @param {object} [options]
 */
export function formatMatterMoney(value, currencyOrMatter = 'USD', options = {}) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return formatMatterMoney(0, currencyOrMatter, options);
  }
  const currency =
    currencyOrMatter && typeof currencyOrMatter === 'object'
      ? normalizeMatterCurrency(
          currencyOrMatter.Currency || currencyOrMatter.currency,
          options.fallback || 'USD'
        )
      : normalizeMatterCurrency(currencyOrMatter, options.fallback || 'USD');
  const maximumFractionDigits =
    options.maximumFractionDigits != null ? options.maximumFractionDigits : 2;
  const minimumFractionDigits =
    options.minimumFractionDigits != null
      ? options.minimumFractionDigits
      : Math.min(2, maximumFractionDigits);
  try {
    return new Intl.NumberFormat(options.locale || 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits,
      minimumFractionDigits,
      notation: options.notation,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Read currency from a matter-like object.
 * @param {object} [matter]
 * @param {string} [fallback]
 */
export function matterCurrency(matter = {}, fallback = 'USD') {
  return normalizeMatterCurrency(matter?.Currency ?? matter?.currency, fallback);
}

/**
 * Sum amounts by demand currency and format each group.
 * Avoids collapsing mixed currencies into a single incorrect symbol.
 * @param {object[]} [matters]
 * @param {(matter: object) => unknown} [amountPicker]
 * @param {object} [options]
 */
export function formatMattersMoneySum(
  matters = [],
  amountPicker = (matter) => matter?.Amount_Owed,
  options = {}
) {
  const groups = new Map();
  for (const matter of matters || []) {
    if (!matter) continue;
    const amount = Number(amountPicker(matter) || 0);
    if (!Number.isFinite(amount)) continue;
    const currency = matterCurrency(matter, options.fallback || 'USD');
    groups.set(currency, (groups.get(currency) || 0) + amount);
  }
  if (!groups.size) {
    return formatMatterMoney(0, options.fallback || 'USD', options);
  }
  return [...groups.entries()]
    .map(([currency, sum]) => formatMatterMoney(sum, currency, options))
    .join(' · ');
}
